import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import GroupIcon from '@mui/icons-material/Group';
import { COLORS } from '../../constants/radar';
import { employeesApi } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import CompletionProgressBar from '../../components/common/CompletionProgressBar';
import { useAlert } from '../../contexts/AlertContext';
import { useAuth } from '../../contexts/AuthContext';
import { ASSIGNMENT_STATUSES } from '../../constants/assignmentStatuses';
import { FEEDBACK_SESSION_STATUSES } from '../../constants/feedbackSessionStatuses';
import { REVIEW_TYPES } from '../../constants/reviewTypes';
import useFeedbackResultsStore from '../../store/feedbackResultsStore';
import useFeedbackSessionsStore from '../../store/feedbackSessionsStore';

// Dark blue color theme for the team card
const TEAM_CARD_COLOR = '#2B436B';

const TeamPendingActionsCard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const { showAlert } = useAlert();
  const { user } = useAuth();

  const feedbackResultsResetState = useFeedbackResultsStore(state => state.resetState);
  const feedbackResultsSetStatuses = useFeedbackResultsStore(state => state.setSelectedAssignmentStatuses);

  const feedbackSessionsResetState = useFeedbackSessionsStore(state => state.resetState);
  const feedbackSessionsSetStatuses = useFeedbackSessionsStore(state => state.setSelectedStatuses);
  const feedbackSessionsSetReviewTypes = useFeedbackSessionsStore(state => state.setSelectedReviewTypes);

  // Check if user has any of the required roles to see this card
  const employee = user?.employee;
  const hasRequiredRole = employee?.isAdmin ||
    employee?.isBusinessUnitManager ||
    employee?.isClusterManager ||
    employee?.isPeoplePartner ||
    employee?.isTeamManager;

  useEffect(() => {
    // Skip fetching if user doesn't have required role
    if (!hasRequiredRole) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await employeesApi.getTeamNotifications();
        setData(response);
      } catch (err) {
        console.error('Error fetching team notifications:', err);
        showAlert('Failed to load team pending actions', 'error', err);
        setData({
          pendingActionsCount: 0,
          sessionsCount: 0,
          employeesCount: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [showAlert, hasRequiredRole]);

  // Don't render if user doesn't have required role
  if (!hasRequiredRole) {
    return null;
  }

  const handleViewPendingActions = () => {
    feedbackResultsResetState();
    feedbackResultsSetStatuses([ASSIGNMENT_STATUSES.PENDING_ACTIONS]);
    // Pass fromDetail flag to prevent FeedbackResults from resetting state on mount
    navigate('/feedback-results', { state: { fromDetail: true } });
  };

  const handleViewSessions = () => {
    feedbackSessionsResetState();
    feedbackSessionsSetStatuses([FEEDBACK_SESSION_STATUSES.IN_PROGRESS]);
    feedbackSessionsSetReviewTypes([REVIEW_TYPES.PARALLEL]);
    // Pass fromDetail flag to prevent FeedbackSessions from resetting state on mount
    navigate('/feedback-sessions', { state: { fromDetail: true } });
  };

  if (loading) {
    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: COLORS.BACKGROUND_LIGHT,
          width: 400,
          height: 240,
          mt: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LoadingSpinner message="Loading team actions..." />
      </Card>
    );
  }

  const hasPendingActions = data?.pendingActionsCount > 0;

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: COLORS.BACKGROUND_LIGHT,
        width: 400,
        mt: 1,
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box sx={{
          p: 2,
          backgroundColor: TEAM_CARD_COLOR,
          color: 'white'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Team Feedback Overview
          </Typography>
          <Typography variant="body2">
            {data?.pendingActionsCount || 0} Pending Actions
          </Typography>
        </Box>

        {!hasPendingActions ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            height: 150
          }}>
            <Typography variant="body1" color="text.secondary">
              No pending actions
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your team is all caught up!
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Completion Progress Bar */}
            <CompletionProgressBar percentage={data?.completion?.percentage || 0} withContainer />

            <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
              Pending feedback is blocking session closure and final reports.
            </Typography>

            <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
              These <strong>{data?.pendingActionsCount || 0}</strong> pending actions impact:
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EventNoteIcon sx={{ color: TEAM_CARD_COLOR, fontSize: 18 }} />
                <Typography variant="body2" color="text.primary">
                  <strong>{data?.sessionsCount || 0}</strong> Sessions
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <GroupIcon sx={{ color: TEAM_CARD_COLOR, fontSize: 18 }} />
                <Typography variant="body2" color="text.primary">
                  <strong>{data?.employeesCount || 0}</strong> Individuals
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mt: 3 }}>
              <Chip
                label="Review Pending Actions"
                variant="outlined"
                size="small"
                onClick={handleViewPendingActions}
                sx={{
                  backgroundColor: 'transparent',
                  color: TEAM_CARD_COLOR,
                  borderColor: TEAM_CARD_COLOR,
                  borderWidth: 1,
                  fontWeight: 500,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'rgba(43, 67, 107, 0.04)',
                  },
                }}
              />
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  handleViewSessions();
                }}
                style={{
                  color: TEAM_CARD_COLOR,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                onMouseOut={(e) => e.target.style.textDecoration = 'none'}
              >
                View Sessions →
              </Link>
            </Box>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamPendingActionsCard;
